import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";
import { useEvents } from "@/context/EventsContext";
import { EventGroupCard } from "./EventGroupCard";

const RECENT_WINDOW_MS = 6 * 60 * 60 * 1000; // 6 hours

type RecentEventsCardProps = {
  accountId: string | undefined;
};

export const RecentEventsCard = ({ accountId }: RecentEventsCardProps): React.ReactNode => {
  const { groups, loading } = useEvents();

  const recentGroups = useMemo(() => {
    const cutoff = Date.now() - RECENT_WINDOW_MS;
    return groups.filter(g => new Date(g.latestTime).getTime() >= cutoff);
  }, [groups]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center">
        <div className="grid gap-2">
          <CardTitle className="text-2xl">Recent Events</CardTitle>
          <CardDescription>Order activity from the last 6 hours.</CardDescription>
        </div>
        {accountId && (
          <Button asChild size="sm" className="ml-auto gap-1">
            <Link to={`/account/${accountId}/events`}>
              View All
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {recentGroups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {loading ? "Loading..." : "No events in the last 6 hours."}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {recentGroups.map((group) => (
              <EventGroupCard key={group.key} group={group} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
